/* eslint-disable */
import { Util } from '@libs/utilities/util';
import { IStatsRegressionOptions, IStatsRegressionResult, TypeStatsDataPoint, TypeStatsPredictFn } from './regression.interface';


/**------------------------------------------------------
 * Regression Library
 * ------------------
 * > npm : https://www.npmjs.com/package/regression
 * > git : https://github.com/tom-alexander/regression-js#readme
 */
export abstract class AbstractRegressionLibrary {

	//** Variables */
	private readonly DEFAULT_OPTIONS: IStatsRegressionOptions = {
		order		: 2,
		precision	: 2
	}


	/**------------------------------------------------------
	 * Mathematical Function Algorithms
	 */
	protected linear(dataPoints: TypeStatsDataPoint[], options?: Partial<IStatsRegressionOptions>): IStatsRegressionResult {

		//0 - initialize variables
		const regressionOptions: IStatsRegressionOptions = Util.Function.assignOptions(this.DEFAULT_OPTIONS, options);
		const sum: number[] = [0, 0, 0, 0, 0];
		let   len: number   = 0;

		//1 - update sum value
		for (let n: number = 0; n < dataPoints.length; n++) {
			if (dataPoints[n][1] === null) continue;
			len++;
			sum[0] += dataPoints[n][0];
			sum[1] += dataPoints[n][1];
			sum[2] += dataPoints[n][0] * dataPoints[n][0];
			sum[3] += dataPoints[n][0] * dataPoints[n][1];
			sum[4] += dataPoints[n][1] * dataPoints[n][1];
		}

		//2 - update run, rise, gradient and intercept
		const run		: number = ((len * sum[2]) - (sum[0] * sum[0]));
		const rise		: number = ((len * sum[3]) - (sum[0] * sum[1]));
		const gradient	: number = run === 0 ? 0 : this.round(rise / run, regressionOptions.precision);
		const intercept	: number = this.round((sum[1] / len) - ((gradient * sum[0]) / len), regressionOptions.precision);

		//3 - update predict
		const predictFn: TypeStatsPredictFn = (x: number): TypeStatsDataPoint => ([
			this.round(x, regressionOptions.precision),
			this.round((gradient * x) + intercept, regressionOptions.precision)]
		);
		const points: number[][] = dataPoints.map((point: number[]) => predictFn(point[0]));

		//4 - create function string
		const functionString: string = (intercept === 0)
			? `y = ${gradient}x`
			: `y = ${gradient}x + ${intercept}`;

		//5 - return points and value
		const regressionResult: IStatsRegressionResult = {
			string 	: functionString,
			points 	: points,
			predict	: predictFn,
			equation: [gradient, intercept],
			r2		: this.round(this.determinationCoefficient(dataPoints, points), regressionOptions.precision),
		}
		return regressionResult;
	}

	protected exponential(dataPoints: TypeStatsDataPoint[], options?: Partial<IStatsRegressionOptions>): IStatsRegressionResult {

		//0 - initialize variables
		const regressionOptions: IStatsRegressionOptions = Util.Function.assignOptions(this.DEFAULT_OPTIONS, options);
		const sum: number[] = [0, 0, 0, 0, 0, 0];
		for (let n: number = 0; n < dataPoints.length; n++) {
			if (dataPoints[n][1] !== null) {
				sum[0] += dataPoints[n][0];
				sum[1] += dataPoints[n][1];
				sum[2] += dataPoints[n][0] * dataPoints[n][0] * dataPoints[n][1];
				sum[3] += dataPoints[n][1] * Math.log(dataPoints[n][1]);
				sum[4] += dataPoints[n][0] * dataPoints[n][1] * Math.log(dataPoints[n][1]);
				sum[5] += dataPoints[n][0] * dataPoints[n][1];
			}
		}

		//1 - update denominator
		const denominator	: number = ((sum[1] * sum[2]) - (sum[5] * sum[5]));
		const a				: number = Math.exp(((sum[2] * sum[3]) - (sum[5] * sum[4])) / denominator);
		const b				: number = ((sum[1] * sum[4]) - (sum[5] * sum[3])) / denominator;
		const coefficientA	: number = this.round(a, regressionOptions.precision);
		const coefficientB	: number = this.round(b, regressionOptions.precision);

		const predictFn: TypeStatsPredictFn = (x: number) => ([
			this.round(x, regressionOptions.precision),
			this.round(coefficientA * Math.exp(coefficientB * x), regressionOptions.precision),
		]);

		//2 - update points
		const points: number[][] = dataPoints.map((point: number[]) => predictFn(point[0]));
		const regressionResult: IStatsRegressionResult = {
			string 	: `y = ${coefficientA}e^(${coefficientB}x)`,
			points 	: points,
			predict	: predictFn,
			equation: [coefficientA, coefficientB],
			r2		: this.round(this.determinationCoefficient(dataPoints, points), regressionOptions.precision),
		}
		return regressionResult;
	}

	protected logarithmic(dataPoints: TypeStatsDataPoint[], options?: Partial<IStatsRegressionOptions>): IStatsRegressionResult {

		//0 - initialize variables
		const regressionOptions: IStatsRegressionOptions = Util.Function.assignOptions(this.DEFAULT_OPTIONS, options);
		const sum: number[] = [0, 0, 0, 0];
		const len: number 	= dataPoints.length;

		//1 - update sum value
		for (let n: number = 0; n < len; n++) {
			if (dataPoints[n][1] !== null) {
				sum[0] += Math.log(dataPoints[n][0]);
				sum[1] += dataPoints[n][1] * Math.log(dataPoints[n][0]);
				sum[2] += dataPoints[n][1];
				sum[3] += (Math.log(dataPoints[n][0]) ** 2);
			}
		}

		//2 - update coefficient
		const a			  : number = ((len * sum[1]) - (sum[2] * sum[0])) / ((len * sum[3]) - (sum[0] * sum[0]));
		const coefficientB: number = this.round(a, regressionOptions.precision);
		const coefficientA: number = this.round((sum[2] - (coefficientB * sum[0])) / len, regressionOptions.precision);

		const predictFn: TypeStatsPredictFn = (x: number) => ([
			this.round(x, regressionOptions.precision),
			this.round(this.round(coefficientA + (coefficientB * Math.log(x)), regressionOptions.precision), regressionOptions.precision),
		]);

		//3 - update points
		const points		  : number[][] = dataPoints.map((point: number[]) => predictFn(point[0]));
		const regressionResult: IStatsRegressionResult = {
			string 	: `y = ${coefficientA} + ${coefficientB} ln(x)`,
			points 	: points,
			predict	: predictFn,
			equation: [coefficientA, coefficientB],
			r2		: this.round(this.determinationCoefficient(dataPoints, points), regressionOptions.precision),
		}
		return regressionResult;
	}

	protected power(dataPoints: TypeStatsDataPoint[], options?: Partial<IStatsRegressionOptions>): IStatsRegressionResult {

		//0 - initialize variables
		const regressionOptions: IStatsRegressionOptions = Util.Function.assignOptions(this.DEFAULT_OPTIONS, options);
		const sum: number[] = [0, 0, 0, 0, 0];
		const len: number 	= dataPoints.length;

		//1 - update sum value
		for (let n: number = 0; n < len; n++) {
			if (dataPoints[n][1] !== null) {
				sum[0] += Math.log(dataPoints[n][0]);
				sum[1] += Math.log(dataPoints[n][1]) * Math.log(dataPoints[n][0]);
				sum[2] += Math.log(dataPoints[n][1]);
				sum[3] += (Math.log(dataPoints[n][0]) ** 2);
			}
		}

		//2 - update coefficient
		const b			  : number = ((len * sum[1]) - (sum[0] * sum[2])) / ((len * sum[3]) - (sum[0] ** 2));
		const a			  : number = ((sum[2] - (b * sum[0])) / len);
		const coefficientA: number = this.round(Math.exp(a), regressionOptions.precision);
		const coefficientB: number = this.round(b, regressionOptions.precision);

		//3 - update predict
		const predictFn: TypeStatsPredictFn = (x: number): TypeStatsDataPoint => ([
			this.round(x, regressionOptions.precision),
			this.round(this.round(coefficientA * (x ** coefficientB), regressionOptions.precision), regressionOptions.precision),
		]);
		const points: number[][] = dataPoints.map((point: number[]) => predictFn(point[0]));

		//4 - update points
		const regressionResult: IStatsRegressionResult = {
			string 	: `y = ${coefficientA}x^${coefficientB}`,
			points 	: points,
			predict	: predictFn,
			equation: [coefficientA, coefficientB],
			r2		: this.round(this.determinationCoefficient(dataPoints, points), regressionOptions.precision),
		}
		return regressionResult;
	}

	protected polynomial(dataPoints: TypeStatsDataPoint[], options?: Partial<IStatsRegressionOptions>): IStatsRegressionResult {

		//0 - initialize variables
		const regressionOptions: IStatsRegressionOptions = Util.Function.assignOptions(this.DEFAULT_OPTIONS, options);
		const lhs	: number[] 	 = [];
		const rhs	: number[][] = [];
		let   a		: number   	 = 0;
		let   b		: number	 = 0;
		const len	: number	 = dataPoints.length;
		const k		: number	 = regressionOptions.order + 1;

		//1 - update lhs
		for (let i: number = 0; i < k; i++) {
			for (let l: number = 0; l < len; l++) {
				if (dataPoints[l][1] !== null) {
					a += (dataPoints[l][0] ** i) * dataPoints[l][1];
				}
			}
			lhs.push(a);
			a = 0;
			const c: number[] = [];
			for (let j: number = 0; j < k; j++) {
				for (let l: number = 0; l < len; l++) {
					if (dataPoints[l][1] !== null) {
						b += dataPoints[l][0] ** (i + j);
					}
				}
				c.push(b);
				b = 0;
			}
			rhs.push(c);
		}
		rhs.push(lhs);

		//2 - update coefficients and predict number
		const coefficients: number[] 	  = this.gaussianElimination(rhs, k).map((v: number) => this.round(v, regressionOptions.precision));
		const predictFn   : TypeStatsPredictFn = (x: number): TypeStatsDataPoint => ([
			this.round(x, regressionOptions.precision),
			this.round(
				coefficients.reduce((sum: number, coefficient: number, power: number) => sum + (coefficient * (x ** power)), 0),
				regressionOptions.precision,
			),
		]);

		//3 - update points
		const points: number[][] = dataPoints.map((point: number[]) => predictFn(point[0]));
		let functionString: string = 'y = ';
		for (let i: number = coefficients.length - 1; i >= 0; i--) {
			if (i > 1) {
				functionString += `${coefficients[i]}x^${i} + `;
			} else if (i === 1) {
				functionString += `${coefficients[i]}x + `;
			} else {
				functionString += coefficients[i];
			}
		}

		//4 - return data
		const regressionResult: IStatsRegressionResult = {
			string 	: functionString,
			points 	: points,
			predict	: predictFn,
			equation: [...coefficients].reverse(),
			r2		: this.round(this.determinationCoefficient(dataPoints, points), regressionOptions.precision),
		}
		return regressionResult;
	}


	/**------------------------------------------------------
	 * Helper Function
	 */
	private determinationCoefficient(dataPoints: TypeStatsDataPoint[], results: number[][]): number {

		//0 - update predictions and observations
		const predictions : number[][] = [];
		const observations: number[][] = [];
		dataPoints.forEach((dataPoint: number[], i: number) => {
			if (dataPoint[1] === null) {
				observations.push(dataPoint);
				predictions.push(results[i]);
			}
		});

		//1 - calculate some and mean
		const sum : number = observations.reduce((a: number, observation: number[]) => a + observation[1], 0);
		const mean: number = sum / observations.length;

		//2 - get standardDeviationYY
		const standardDeviationYY: number = observations.reduce((a: number, observation: number[]) => {
			const difference: number = observation[1] - mean;
			return a + (difference * difference);
		}, 0);

		//3 - get sumOfSquaresError
		const sumOfSquaresError: number = observations.reduce((accumulator: number, observation: number[], index: number) => {
			const prediction: number[] 	= predictions[index];
			const residual	: number 	= observation[1] - prediction[1];
			return accumulator + (residual * residual);
		}, 0);

		//4 - return coefficient
		return 1 - (sumOfSquaresError / standardDeviationYY);
	}

	private gaussianElimination(input: number[][], order: number): number[] {

		//0 - initialize variables
		const matrix		: number[][] = input;
		const n				: number 	 = input.length - 1;
		const coefficients	: number[] 	 = [order];

		//1 - update max row and matrix
		for (let i: number = 0; i < n; i++) {
			let maxRow: number = i;
			for (let j: number = i + 1; j < n; j++) {
				if (Math.abs(matrix[i][j]) > Math.abs(matrix[i][maxRow])) {
					maxRow = j;
				}
			}

			for (let k: number = i; k < n + 1; k++) {
				const tmp: number = matrix[k][i];
				matrix[k][i] 	  = matrix[k][maxRow];
				matrix[k][maxRow] = tmp;
			}

			for (let j: number = i + 1; j < n; j++) {
				for (let k: number = n; k >= i; k--) {
					matrix[k][j]  -= (matrix[k][i] * matrix[i][j]) / matrix[i][i];
				}
			}
		}

		//2 - update coefficients
		for (let j: number = n - 1; j >= 0; j--) {
			let total: number = 0;
			for (let k: number = j + 1; k < n; k++) {
				total += matrix[k][j] * coefficients[k];
			}
			coefficients[j] = (matrix[n][j] - total) / matrix[j][j];
		}

		return coefficients;
	}

	//** Return rounded value */
	private round(number: number, precision: number): number {
		const factor: number = 10 ** precision;
		return Math.round(number * factor) / factor;
	}
}
